import {useEffect, useState, useMemo} from "react";
import {useSearchParams} from "react-router";
import {BoundSearchQuery} from "../types/common/BoundSearchQuery";

export interface UseBindSearchQueryOptions {
  replace?: boolean;
}

/**
 * Binds a collection of Search Params in the URL to state in react.
 * Use this when you need to update multiple parameters at a time.
 * @param defaultQuery default values to initiate react state with, removes param from URL if getter is default
 * @param options configuration options
 * @param {boolean} [options.replace=true] if true, replaces current history entry instead of pushing
 * new one. Defaults to true: filters update the query string on every keystroke, and one history
 * entry per character makes the back button unusable. Pass `false` for params where each value
 * genuinely deserves its own history entry.
 *
 * @see {@link useBindSearchParam} for single param binding.
 */
export const useBindSearchQuery: (
  defaultQuery: BoundSearchQuery,
  options?: UseBindSearchQueryOptions
) => [BoundSearchQuery, (newValue: BoundSearchQuery) => void] = (
  defaultQuery,
  options: UseBindSearchQueryOptions = {replace: true}
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // shouldn't expose setter to control react state more predictably

  const defaultValues: BoundSearchQuery = useMemo(() => (defaultQuery), [defaultQuery]);

  const [getter, setter] = useState<BoundSearchQuery>(defaultValues);

  // calling the wrapped setting will change searchParams which will trigger the following useEffect to update
  // internal state to match query params. This way the searchParams becomes the single source of truth
  const wrappedSetter = (newQuery: BoundSearchQuery) => {

    //we only want to call setSearchParams if something has changed
    setSearchParams(prevSearchParams => {
      for(const paramName in newQuery) {
        const value = newQuery[paramName];
        if (
          value !== null &&
          value !== undefined &&
          value !== defaultValues[paramName]
        ) {
          prevSearchParams.set(paramName, value as string);
        } else if (searchParams.has(paramName)) {
          prevSearchParams.delete(paramName);
        }
      }
      return prevSearchParams;
    }, { replace: options.replace });
  }

  // get the params from searchParams
  useEffect(() => {
    const result: BoundSearchQuery = {};
    let hasValues: boolean = false;
    for (const [key, value] of searchParams.entries()) {
      if (
        value !== "undefined" &&
        value !== "null" &&
        (!defaultValues.hasOwnProperty(key) || defaultValues[key] != value)
      ) {
        result[key] = value;
        hasValues = true;
      }
    }

    setter(hasValues ? {...defaultValues, ...result} : defaultValues);
  }, [setter, searchParams, defaultValues]);

  return [getter, wrappedSetter];
}
